// =============================================================================
// zaplanowanaZmiana — zdanie „Zmiana na X od <data>" na karcie modułu
// i abonamentu (decyzja a właściciela, 02.10.2026, runda 4).
//
// Obniżka planu wchodzi od NASTĘPNEGO okresu rozliczeniowego: klient korzysta
// z droższego do końca opłaconego okresu, a Stripe trzyma nową cenę
// w harmonogramie. Serwer zapisuje to w wierszu (`zmiana_planu_na`,
// `zmiana_planu_okres`, `zmiana_planu_od`, `zmiana_planu_z` w
// `module_subscriptions` i `stripe_subscriptions`) i liczy te kolumny od nowa
// przy każdym zdarzeniu Stripe — panel tylko je czyta. Bez tego zdania klient
// widziałby „Plan: Professional" i nie wiedziałby, że za dwa tygodnie
// zostanie mu Basic.
//
// Runda 5: nazwa pakietu z CENNIKA, nie slug („Podstawowy", a nie „Basic" obok
// „Plan: Profesjonalny"), a zmiana samego okresu (ten sam pakiet, miesiąc →
// rok) to „Rozliczenie rocznie od …", nie „Zmiana na Optymalny", co czytało
// się jak zmiana pakietu.
// =============================================================================

/** Wiersz subskrypcji w zakresie, który czyta zdanie. */
export interface WierszZeZmiana {
  zmiana_planu_na?: string | null;
  zmiana_planu_okres?: string | null;
  zmiana_planu_od?: string | null;
  /** Pakiet/plan sprzed zmiany (runda 5) — ten sam co `na` = zmienia się tylko okres. */
  zmiana_planu_z?: string | null;
}

/** Nazwy planów abonamentu (i zapas, gdy cennik pakietu jeszcze się nie wczytał). */
const NAZWY: Record<string, string> = {
  free: 'Darmowy',
  starter: 'Starter',
  professional: 'Professional',
  enterprise: 'Enterprise',
  basic: 'Podstawowy',
  optimal: 'Optymalny',
};

const OKRESY: Record<string, string> = { month: 'miesięcznie', year: 'rocznie' };

export interface OpcjeZdania {
  /** Nazwa pakietu modułu z cennika (`module_pricing_tiers.name_pl`) albo `null`. */
  nazwa?: (slug: string) => string | null | undefined;
  teraz?: Date;
}

/**
 * „Zmiana na Podstawowy (miesięcznie) od 15.10.2026", „Rozliczenie rocznie od
 * 1.11.2026" (ten sam pakiet, inny okres) albo `null`, gdy nic nie jest
 * zaplanowane — także gdy data już minęła (webhook przejścia fazy jeszcze nie
 * doszedł): wtedy zmiana już obowiązuje, a nie „będzie".
 */
export function zdanieZaplanowanejZmiany(
  wiersz: WierszZeZmiana | null | undefined,
  opcje: OpcjeZdania = {}
): string | null {
  const teraz = opcje.teraz ?? new Date();
  const od = wiersz?.zmiana_planu_od ? Date.parse(wiersz.zmiana_planu_od) : NaN;
  if (!Number.isFinite(od) || od <= teraz.getTime()) return null;
  const na = wiersz?.zmiana_planu_na ?? null;
  const okres = OKRESY[wiersz?.zmiana_planu_okres ?? ''];
  const data = new Date(od).toLocaleDateString('pl-PL', { timeZone: 'Europe/Warsaw' });
  if (na && okres && wiersz?.zmiana_planu_z === na) {
    return `Rozliczenie ${okres} od ${data}`;
  }
  const nazwa = na ? (opcje.nazwa?.(na) ?? NAZWY[na] ?? na) : 'inny plan';
  return `Zmiana na ${nazwa}${okres ? ` (${okres})` : ''} od ${data}`;
}

/**
 * Okres rozliczeniowy abonamentu z długości bieżącego okresu (runda 5:
 * przełącznik na stronie rozliczeń startował zawsze na „Miesięcznie", więc
 * klient roczny klikający WYŻSZY plan dostawał zmianę odłożoną nawet o rok —
 * rok → miesiąc to obniżka od następnego okresu). `null`, gdy nie wiadomo.
 */
export function okresAbonamentu(
  sub: { current_period_start?: string | null; current_period_end?: string | null } | null
): 'monthly' | 'yearly' | null {
  const start = sub?.current_period_start ? Date.parse(sub.current_period_start) : NaN;
  const koniec = sub?.current_period_end ? Date.parse(sub.current_period_end) : NaN;
  if (!Number.isFinite(start) || !Number.isFinite(koniec) || koniec <= start) return null;
  const dni = (koniec - start) / 86_400_000;
  return dni > 62 ? 'yearly' : 'monthly';
}

/**
 * Zapowiedź PRZED kliknięciem „Wybierz plan" (runda 5): wybór krótszego okresu
 * niż obecny (rok → miesiąc) jest obniżką od następnego okresu, także przy
 * planie wyższym — klient ma to wiedzieć, zanim kliknie, a nie zobaczyć pod
 * kartą po przekierowaniu. `null`, gdy zmiana wejdzie od razu.
 */
export function zapowiedzZmianyOkresu(
  sub: { current_period_start?: string | null; current_period_end?: string | null } | null,
  wybrany: 'monthly' | 'yearly'
): string | null {
  const obecny = okresAbonamentu(sub);
  if (obecny !== 'yearly' || wybrany !== 'monthly' || !sub?.current_period_end) return null;
  const data = new Date(sub.current_period_end).toLocaleDateString('pl-PL', {
    timeZone: 'Europe/Warsaw',
  });
  return `Zmiana na rozliczenie miesięczne wejdzie od ${data} — do tego dnia trwa opłacony rok.`;
}
